import { useCallback, useEffect, useRef } from 'react';

import { type ClientId } from '../client/bridge-api-client-types';
import type { CommandInvocation } from '../command/bridge-api-command-invocation';
import {
	type CommandConstructor,
	type CommandProperties,
	type CommandPropertiesWithKnownSender,
	type CommandResult,
} from '../command/bridge-api-command-types';
import { type RovoContentBridgeSetupHandler } from '../create-rovo-bridge-api-client';
import { type RovoContentBridgeAPI } from '../rovo-bridge-api';
import { type RovoContentBridgeAPIShape } from '../rovo-bridge-api-interface';
import { getBrowserBridgeAPI } from '../rovo-bridge-api-singleton';

/**
 * React hook API companion for the {@link RovoContentBridgeAPI}.
 *
 * Exposes render-safe mechanisms for a bridge API client, with automated lifecycle management on teardown.
 */
export type RovoContentBridgeHookAPI = {
	/**
	 * ID of the bridge API singleton being used. Should be the same across 1 JS context.
	 */
	id: string;
	/**
	 * Mark a client as ready to accept or send messages.
	 *
	 * @description Any handlers you setup will be instantiated here, to ensure that a client only
	 * ever communicates they are ready AFTER all handlers have been configured. This is to avoid
	 * the possibility of race conditions where a client says they are ready, but haven't configured
	 * a handler for a message which a remote client (now thinking this client is ready) sends to it.
	 */
	ready: VoidFunction;
	/**
	 * Mark a client as in the process of being destroyed - will no longer receive any messages.
	 *
	 * @description This will be performed by default when the hook is unmounted along with the React
	 * tree it is mounted in. ONLY use this method if it a necessity for you to more granularly control
	 * tearing down of a specific bridge API client. E.g., if you are an `iframe` or need to hook into a
	 * distinct part of the React lifecycle when unmounting a bridge API client.
	 */
	teardown: VoidFunction;
	/**
	 * Send a command to another bridge API client.
	 *
	 * @description To setup handlers, use {@link useRovoContentBridgeAPI} with a setup function.
	 */
	send: <C extends CommandProperties, R extends CommandResult>(
		command: CommandConstructor<C, R>,
		params: CommandPropertiesWithKnownSender<C>,
	) => CommandInvocation<R>;
};

/**
 * Provides a stable, render-safe API for interacting with the Rovo content bridge API.
 *
 * @description Offers an opinionated API which (1) enforces all command handlers are registered before a
 * client marks itself as ready, and (2) provides better DevX around sending and receiving commands.
 * @description Will tear down the provided {@link clientId} on unmount of this React tree. Includes
 * automated teardown of all command handlers registered by this client ID.
 *
 * @param clientId unique identifier for the client constructing the bridge API
 * @param setupHandlers a callback inside of which a client can safely setup command handlers before receiving
 * any messages from a remote client to handle the message which was sent to it
 * @param api =
 * @returns {RovoContentBridgeHookAPI} singleton instance
 */
export const useRovoContentBridgeAPI = (
	clientId: ClientId,
	setupHandlers?: RovoContentBridgeSetupHandler,
	apiInstance: RovoContentBridgeAPIShape = getBrowserBridgeAPI(),
): RovoContentBridgeHookAPI => {
	const refApi = useRef<RovoContentBridgeAPIShape>(apiInstance);
	const refReady = useRef(false);
	const refMount = useRef(setupHandlers);

	const api = refApi.current;

	const setup = useCallback(() => {
		// register all handlers
		// devx: provide current `clientId`
		setupHandlers?.((command, handler) => api.on(clientId, command, handler));
	}, [clientId, setupHandlers, api]);

	const ready = useCallback(() => {
		// setup all handlers
		setup();

		// mark client as ready
		api.ready(clientId);

		// now, the client can receive messages AND handle them
		refReady.current = true;
	}, [clientId, setup, api]);

	const send = useCallback(
		<C extends CommandProperties, R extends CommandResult>(
			command: CommandConstructor<C, R>,
			params: CommandPropertiesWithKnownSender<C>,
		): CommandInvocation<R> => {
			// devx: provide current `clientId`
			return api.send(command, { ...params, senderId: clientId } as C);
		},
		[clientId, api],
	);

	const teardown = useCallback(() => {
		// devx: provide current `clientId`
		return api.teardown(clientId);
	}, [clientId, api]);

	useEffect(() => {
		// teardown if component tree is being destroyed
		// note: will also tear down all command handlers on your behalf
		return () => api.teardown(clientId);
	}, [clientId, api]);

	/**
	 * If we have already mounted our handlers once and marked ourselves as ready, then this
	 * bridge API client is already capable of receiving & handling commands. On subsequent renders
	 * if our {@link setupHandlers} reference changes, we need to make sure we run our setup again.
	 *
	 * This will ensure the newest state of every handler is being used, with no stale references
	 * in closures, as can often occur with initialise-only-once code in React.
	 */
	if (refReady.current && refMount.current !== setupHandlers) {
		setup();
	}

	// so we can compare on next render
	refMount.current = setupHandlers;

	return { id: api.id, ready, send, teardown };
};
